import { useState } from 'react';

import { CasBaseDialog, CasTable } from 'src/compass/shared/components';
import CasLink from 'src/compass/shared/components/CasLink/CasLink';
import CasLoader from 'src/compass/shared/components/CasLoader/CasLoader';
import ICasRenderCellProps from 'src/compass/shared/components/CasTable/ICasRenderCellProps';
import useRetrieve from 'src/compass/shared/hooks/reactQuery/useRetrieve';

import { getColumnColorByDaysLeft } from '../ClaimListing/ClaimListing';
import SharedClaimNumberDialog from './SharedClaimNumberDialog';

export const REVIEWED_MULTI_SCE_URL = 'Sce/Claims/ReviewedMultiSce';

interface Props {
  onClose: VoidFunction;
}

// Claim numbers with an SCE in Reviewed status. The server sorts the ready ones (every SCE
// reviewed) first, so they can be attached together.
export default function ReviewedMultiSceClaimsDialog({ onClose }: Props) {
  const [sharedClaimNumber, setSharedClaimNumber] = useState<number | null>(null);

  // isLoading, not isFetching: the Docusign Review tab already holds this query (same key), so the
  // dialog opens on that answer at once and refreshes it in the background.
  const { data, isLoading, refetch } = useRetrieve({ url: REVIEWED_MULTI_SCE_URL });

  const columns = [
    {
      field: 'claimNumber',
      label: 'Claim#',
      render: ({ row }: ICasRenderCellProps) => (
        <CasLink onClick={() => setSharedClaimNumber(row.claimNumber)}>{row.claimNumber}</CasLink>
      ),
    },
    { field: 'insuredName' },
    { field: 'daysLeft', label: 'Days Left', sx: getColumnColorByDaysLeft },
    { field: 'sceCount', label: '#SCEs' },
    {
      // An already-Attached SCE counts as reviewed.
      field: 'reviewedOrAttachedCount',
      label: '#Reviewed',
      sx: (row: Record<string, any>) => (row?.allReviewed ? { backgroundColor: '#d8ffd8' } : {}),
    },
  ];

  return (
    <CasBaseDialog open maxWidth="sm" showCloseIcon title="Reviewed Multi-SCE Claims" onClose={onClose}>
      {isLoading ? (
        <CasLoader />
      ) : (
        <CasTable
          data={{ result: data ?? [] }}
          columns={columns}
          showPagination={false}
          columnsSortable={false}
          includeExport={false}
        />
      )}

      {sharedClaimNumber !== null && (
        <SharedClaimNumberDialog
          claimNumber={sharedClaimNumber}
          // Reviewed SCEs are worked from here, so they check out whether this dialog was opened
          // from the Docusign Review tab or the Dashboard.
          autoCheckout
          onClose={() => {
            setSharedClaimNumber(null);
            // Its rows open their own details, where an SCE can be attached or sent back.
            refetch();
          }}
        />
      )}
    </CasBaseDialog>
  );
}
